
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import { useAuth } from "@/context/AuthContext";

const API_BASE = "https://guardiancloudcare.com/backend/api";

interface WalkInCase {
  [key: string]: any;
  case_number: string;
  child_full_name: string;
  incident_type: string;
  incident_description: string;
  incident_location: string;
  priority_level: string;
  case_status: string;
  created_at: string;
  assigned_social_worker_uid: string | null;
  assigned_social_worker_name: string | null;
}

function show(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }
  return String(value);
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-5 py-4">
        <h2 className="font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2">
        {children}
      </div>
    </section>
  );
}

function DetailItem({
  label,
  value,
}: {
  label: string;
  value: unknown;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
        {show(value)}
      </p>
    </div>
  );
}

export default function SupWalkInCaseDetailPage() {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [walkInCase, setWalkInCase] = useState<WalkInCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCase = useCallback(async () => {
    if (!user?.uid || !caseNumber) {
      setError("Missing supervisor account or case number.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE}/get_supervisor_walkin_case_detail.php` +
          `?supervisor_uid=${encodeURIComponent(user.uid)}` +
          `&case_number=${encodeURIComponent(caseNumber)}`
      );

      const result = await response.json();

      if (!response.ok || !result.success || !result.case) {
        throw new Error(result.message || "Unable to load walk-in case.");
      }

      setWalkInCase(result.case);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to load walk-in case."
      );
    } finally {
      setLoading(false);
    }
  }, [user?.uid, caseNumber]);

  useEffect(() => {
    void loadCase();
  }, [loadCase]);

  if (loading) {
    return (
      <AppLayout
        title="Walk-in Case Details"
        breadcrumb={["Supervisor", "Victim Reports", "Walk-in Case"]}
      >
        <div className="rounded-xl border bg-white p-10 text-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-teal-600" />
          <p className="mt-3 text-sm text-gray-500">
            Loading walk-in case details...
          </p>
        </div>
      </AppLayout>
    );
  }

  if (error || !walkInCase) {
    return (
      <AppLayout
        title="Walk-in Case Details"
        breadcrumb={["Supervisor", "Victim Reports", "Walk-in Case"]}
      >
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => navigate("/sup-victim-reports")}
            className="text-sm text-teal-700 hover:underline"
          >
            <i className="ri-arrow-left-line mr-1" />
            Back to Victim Reports
          </button>

          <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-700">
            <p className="font-semibold">Unable to load walk-in case</p>
            <p className="mt-1 text-sm">{error || "Case not found."}</p>
            <button
              type="button"
              onClick={() => void loadCase()}
              className="mt-4 rounded-lg bg-red-700 px-4 py-2 text-sm text-white"
            >
              Retry
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const c = walkInCase;

  return (
    <AppLayout
      title="Walk-in Case Details"
      breadcrumb={["Supervisor", "Victim Reports", c.case_number]}
    >
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => navigate("/sup-victim-reports")}
          className="text-sm font-medium text-teal-700 hover:underline"
        >
          <i className="ri-arrow-left-line mr-1" />
          Back to Victim Reports
        </button>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400">
                Walk-in Case
              </p>
              <h1 className="mt-1 break-words text-2xl font-bold text-gray-900">
                {c.case_number}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Created: {show(c.created_at)}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-orange-100 px-3 py-1.5 text-xs text-orange-700">
                {show(c.priority_level)}
              </span>
              <span className="rounded-full bg-blue-100 px-3 py-1.5 text-xs text-blue-700">
                {show(c.case_status)}
              </span>
            </div>
          </div>
        </div>

        <DetailSection title="Incident Information">
          <DetailItem label="Incident Type" value={c.incident_type} />
          <DetailItem label="Priority" value={c.priority_level} />
          <DetailItem label="Incident Date" value={c.incident_date} />
          <DetailItem label="Incident Location" value={c.incident_location} />
          <DetailItem label="Referral Source" value={c.referral_source} />
          <DetailItem label="Report Source" value={c.report_source} />
          <div className="md:col-span-2">
            <DetailItem
              label="Incident Description"
              value={c.incident_description}
            />
          </div>
          <DetailItem label="Perpetrator Name" value={c.perpetrator_name} />
          <DetailItem
            label="Perpetrator Relationship"
            value={c.perpetrator_relation}
          />
          <DetailItem label="Witness" value={c.witness_name} />
          <DetailItem label="Prior Incidents" value={c.prior_incidents} />
          <DetailItem label="Case Status" value={c.case_status} />
          <div className="md:col-span-2">
            <DetailItem label="Progress Note" value={c.progress_note} />
          </div>
        </DetailSection>

        <DetailSection title="Child Information">
          <DetailItem label="Full Name" value={c.child_full_name} />
          <DetailItem label="Nickname" value={c.child_nickname} />
          <DetailItem label="Birth Date" value={c.birth_date} />
          <DetailItem label="Age" value={c.age} />
          <DetailItem label="Gender" value={c.gender} />
          <DetailItem label="Nationality" value={c.nationality} />
          <DetailItem label="Religion" value={c.religion} />
          <DetailItem label="Place of Birth" value={c.place_of_birth} />
          <DetailItem label="Civil Status" value={c.civil_status} />
          <DetailItem label="Child Position" value={c.child_position} />
          <DetailItem label="Address" value={c.child_address} />
          <DetailItem label="Barangay" value={c.child_barangay} />
          <DetailItem label="Municipality" value={c.child_municipality} />
          <DetailItem label="Province" value={c.child_province} />
          <DetailItem label="Beneficiary Category" value={c.beneficiary_category} />
          <DetailItem label="Other Relevant Information" value={c.other_relevant_info} />
          <DetailItem label="Reason for Assistance" value={c.reason_for_assistance} />
        </DetailSection>

        <DetailSection title="School Information">
          <DetailItem label="School" value={c.school_name} />
          <DetailItem label="Grade Level" value={c.grade_level} />
          <DetailItem label="Section" value={c.section_name} />
        </DetailSection>

        <DetailSection title="Family Information">
          <DetailItem label="Father's Name" value={c.father_name} />
          <DetailItem label="Mother's Name" value={c.mother_name} />
          <DetailItem label="Siblings" value={c.siblings} />
          <DetailItem label="Solo Parent" value={c.solo_parent} />
        </DetailSection>

        <DetailSection title="Guardian Information">
          <DetailItem label="Guardian Name" value={c.guardian_name} />
          <DetailItem label="Relationship" value={c.guardian_relationship} />
          <DetailItem label="Contact Number" value={c.guardian_contact} />
          <DetailItem label="Guardian Address" value={c.guardian_address} />
        </DetailSection>

        <DetailSection title="Medical Information">
          <DetailItem label="Medical Conditions" value={c.medical_conditions} />
          <DetailItem label="Medications" value={c.medications} />
          <DetailItem label="With Disability" value={c.with_disability} />
        </DetailSection>

        <DetailSection title="Case Assignment">
          <DetailItem
            label="Assigned Social Worker"
            value={c.assigned_social_worker_name}
          />
          <DetailItem
            label="Social Worker UID"
            value={c.assigned_social_worker_uid}
          />
          <DetailItem label="Case Number" value={c.case_number} />
          <DetailItem label="Intake Reference" value={c.uid} />
        </DetailSection>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <i className="ri-shield-check-line text-xl text-blue-600" />
            <div>
              <p className="font-semibold text-blue-800">
                Confidential Case Information
              </p>
              <p className="mt-1 text-sm leading-6 text-blue-700">
                This page contains sensitive child-protection information.
                Access must be limited to authorized personnel.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
